import React, { forwardRef, useMemo } from 'react';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';

// Turns any cell value into something comparable: dates (Date, {date}, ISO,
// DD/MM/YYYY) → timestamp, booleans/numbers → number, the rest → lowercase text.
export const toSortableValue = (value) => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'object') {
    if ('date' in value) return toSortableValue(value.date);
    return toSortableValue(value.label ?? value.name ?? value.nom ?? value.value ?? null);
  }
  const text = String(value).trim();
  if (text === '' || text === '—' || text === '-' || text.toUpperCase() === 'N/A') return null;
  const fr = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
  if (fr) return Date.UTC(+fr[3], +fr[2] - 1, +fr[1], +(fr[4] || 0), +(fr[5] || 0));
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const time = Date.parse(text.replace(' ', 'T'));
    if (!Number.isNaN(time)) return time;
  }
  return text.toLowerCase();
};

const collator = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });

// Empty values always end up after filled ones in ascending order.
export const smartSortComparator = (a, b) => {
  const va = toSortableValue(a);
  const vb = toSortableValue(b);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  if (typeof va === 'number') return -1;
  if (typeof vb === 'number') return 1;
  return collator.compare(va, vb);
};

/**
 * Drop-in replacement for MUI's DataGrid: every column without its own
 * sortComparator gets one that understands the CRM's dates and mixed values,
 * so client-side sorting works the same way in every table.
 */
export const DataGrid = forwardRef(function SortableDataGrid({ columns, ...props }, ref) {
  const sortableColumns = useMemo(
    () => (columns || []).map((col) => (col.sortComparator ? col : { ...col, sortComparator: smartSortComparator })),
    [columns],
  );
  return <MuiDataGrid ref={ref} columns={sortableColumns} {...props} />;
});

export default DataGrid;
